import React, { useState, useEffect } from 'react';
import { usersAPI, customersAPI, skillsAPI } from '../services/api';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showSkillsModal, setShowSkillsModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSkills, setUserSkills] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    full_name: '',
    password: '',
    customer_id: '',
    is_active: true,
    is_admin: false,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersData, customersData, skillsData] = await Promise.all([
        usersAPI.getUsers(),
        customersAPI.getCustomers(),
        skillsAPI.getSkills(),
      ]);
      setUsers(usersData);
      setCustomers(customersData);
      setSkills(skillsData);
    } catch (error) {
      console.error('Failed to load data:', error);
      alert('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingUser(null);
    setFormData({
      username: '', email: '', full_name: '', password: '',
      customer_id: '', is_active: true, is_admin: false,
    });
    setShowModal(true);
  };

  const handleEdit = async (user) => {
    setShowPassword(false);
    try {
      const userData = await usersAPI.getUser(user._id);
      setEditingUser(userData);
      setFormData({
        username: userData.username,
        email: userData.email,
        full_name: userData.full_name,
        password: '',
        customer_id: userData.customer_id || '',
        is_active: userData.is_active,
        is_admin: userData.is_admin,
      });
    } catch (error) {
      setEditingUser(user);
      setFormData({
        username: user.username, email: user.email, full_name: user.full_name,
        password: '', customer_id: user.customer_id || '',
        is_active: user.is_active, is_admin: user.is_admin,
      });
    }
    setShowModal(true);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await usersAPI.deleteUser(userId);
      alert('User deleted successfully');
      loadData();
    } catch (error) {
      alert('Failed to delete user');
    }
  };

  const handleManageSkills = async (user) => {
    setSelectedUser(user);
    try {
      const skillsData = await usersAPI.getUserSkills(user._id);
      setUserSkills(skillsData);
      setShowSkillsModal(true);
    } catch (error) {
      alert('Failed to load skills');
    }
  };

  const handleAddSkill = async (skillId) => {
    if (!selectedUser) return;
    try {
      await usersAPI.addUserSkill(selectedUser._id, skillId);
      const skillsData = await usersAPI.getUserSkills(selectedUser._id);
      setUserSkills(skillsData);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to add skill');
    }
  };

  const handleRemoveSkill = async (skillId) => {
    if (!selectedUser) return;
    try {
      await usersAPI.removeUserSkill(selectedUser._id, skillId);
      setUserSkills(userSkills.filter(s => s.id !== skillId));
    } catch (error) {
      alert('Failed to remove skill');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData };
      if (!submitData.password && editingUser) delete submitData.password;
      if (!submitData.customer_id) submitData.customer_id = null;

      if (editingUser) {
        await usersAPI.updateUser(editingUser._id, submitData);
        alert('User updated successfully');
      } else {
        await usersAPI.createUser(submitData);
        alert('User created successfully');
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save user');
    }
  };

  const getCustomerName = (customerId) => {
    const c = customers.find(c => c._id === customerId);
    return c ? c.name : 'None';
  };

  const skillOptions = skills.filter(s => !userSkills.find(us => us.id === s._id));

  if (loading) {
    return <div style={styles.container}><p>Loading...</p></div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.pageTitle}>User Management</h1>
        <button style={styles.createBtn} onClick={handleCreate}>Create User</button>
      </div>

      <div style={styles.tableSection}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.tableHeader}>
              <th style={styles.th}>Username</th>
              <th style={styles.th}>Email</th>
              <th style={styles.th}>Full Name</th>
              <th style={styles.th}>Customer</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Role</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user._id} style={styles.tableRow}>
                <td style={styles.td}>{user.username}</td>
                <td style={styles.td}>{user.email}</td>
                <td style={styles.td}>{user.full_name}</td>
                <td style={styles.td}>{getCustomerName(user.customer_id)}</td>
                <td style={styles.td}>
                  <span style={{ ...styles.badge, backgroundColor: user.is_active ? '#27ae60' : '#e74c3c' }}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={styles.td}>{user.is_admin ? 'Admin' : 'User'}</td>
                <td style={styles.td}>
                  <button style={styles.actionBtn} onClick={() => handleManageSkills(user)}>Skills</button>
                  <button style={styles.actionBtn} onClick={() => handleEdit(user)}>Edit</button>
                  <button style={{ ...styles.actionBtn, ...styles.deleteBtn }} onClick={() => handleDelete(user._id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h2>{editingUser ? 'Edit User' : 'Create User'}</h2>
            <form onSubmit={handleSubmit} style={styles.form}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Username</label>
                <input type="text" value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} style={styles.input} required />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Email</label>
                <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} style={styles.input} required />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Full Name</label>
                <input type="text" value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} style={styles.input} required />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Password {editingUser && '(leave blank to keep current)'}</label>
                <div style={styles.passwordWrapper}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    style={styles.passwordInput}
                    placeholder={editingUser ? 'Leave empty to keep current' : ''}
                    required={!editingUser}
                  />
                  <button type="button" style={styles.eyeBtn} onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                    {showPassword ? eyeOffSvg : eyeSvg}
                  </button>
                </div>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Customer</label>
                <select value={formData.customer_id} onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })} style={styles.input}>
                  <option value="">None</option>
                  {customers.map((c) => (
                    <option key={c._id} value={c._id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.checkboxLabel}>
                  <input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} style={styles.checkbox} /> Active
                </label>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.checkboxLabel}>
                  <input type="checkbox" checked={formData.is_admin} onChange={(e) => setFormData({ ...formData, is_admin: e.target.checked })} style={styles.checkbox} /> Admin
                </label>
              </div>
              <div style={styles.modalActions}>
                <button type="submit" style={styles.submitBtn}>{editingUser ? 'Update' : 'Create'}</button>
                <button type="button" style={styles.cancelBtn} onClick={() => setShowModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showSkillsModal && selectedUser && (
        <div style={styles.modal}>
          <div style={{ ...styles.modalContent, maxWidth: '600px' }}>
            <h2>Skills of {selectedUser.username}</h2>
            {userSkills.length === 0 ? <p>No skills assigned.</p> : (
              <table style={styles.table}>
                <thead>
                  <tr style={styles.tableHeader}>
                    <th style={styles.th}>Skill</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {userSkills.map((s) => (
                    <tr key={s.id} style={styles.tableRow}>
                      <td style={styles.td}>{s.name}</td>
                      <td style={styles.td}>
                        <button style={{ ...styles.actionBtn, ...styles.deleteBtn }} onClick={() => handleRemoveSkill(s.id)}>Remove</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {skillOptions.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <label style={styles.label}>Add Skill:</label>
                <select style={styles.input} value="" onChange={(e) => { if (e.target.value) handleAddSkill(e.target.value); }}>
                  <option value="">Select skill...</option>
                  {skillOptions.map((s) => (
                    <option key={s._id} value={s._id}>{s.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div style={{ marginTop: '1rem' }}>
              <button style={styles.cancelBtn} onClick={() => setShowSkillsModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const eyeSvg = (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>);
const eyeOffSvg = (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/><path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/></svg>);

const styles = {
  container: { maxWidth: '1400px', margin: '0 auto', padding: '2rem' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' },
  pageTitle: { color: '#60a5fa', margin: 0, fontWeight: '700', textShadow: '0 0 10px rgba(96, 165, 250, 0.3)' },
  createBtn: { padding: '0.75rem 1.5rem', backgroundColor: '#60a5fa', backgroundImage: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', cursor: 'pointer', boxShadow: '0 2px 8px rgba(96, 165, 250, 0.3)' },
  tableSection: { backgroundColor: '#1f2937', padding: '1.5rem', borderRadius: '12px', border: '1px solid #374151', overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: '600px' },
  tableHeader: { backgroundColor: '#111827', backgroundImage: 'linear-gradient(135deg, #111827 0%, #1f2937 100%)', borderBottom: '2px solid #60a5fa' },
  th: { padding: '1rem', textAlign: 'left', fontWeight: '600', letterSpacing: '0.3px', color: '#e2e8f0' },
  tableRow: { borderBottom: '1px solid #374151' },
  td: { padding: '1rem', color: '#9ca3af' },
  badge: { padding: '0.25rem 0.75rem', borderRadius: '12px', color: 'white', fontSize: '12px', fontWeight: '600' },
  actionBtn: { padding: '0.5rem 0.8rem', backgroundColor: '#3498db', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '0.4rem', fontSize: '13px' },
  deleteBtn: { backgroundColor: '#e74c3c' },
  modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '1rem' },
  modalContent: { backgroundColor: '#1f2937', padding: '1.5rem', borderRadius: '12px', width: '90%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto', border: '1px solid #374151', color: '#e2e8f0' },
  form: { display: 'flex', flexDirection: 'column', gap: '1rem' },
  formGroup: { display: 'flex', flexDirection: 'column' },
  label: { marginBottom: '0.5rem', color: '#9ca3af', fontWeight: '500', fontSize: '14px' },
  input: { padding: '0.75rem', border: '1px solid #374151', borderRadius: '8px', fontSize: '16px', backgroundColor: '#111827', color: '#e2e8f0', outline: 'none' },
  passwordWrapper: { display: 'flex', alignItems: 'center', position: 'relative' },
  passwordInput: { padding: '0.75rem', paddingRight: '2.5rem', border: '1px solid #374151', borderRadius: '8px', fontSize: '16px', backgroundColor: '#111827', color: '#e2e8f0', outline: 'none', width: '100%' },
  eyeBtn: { position: 'absolute', right: '0.5rem', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4px' },
  checkboxLabel: { display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#e2e8f0', fontWeight: '500' },
  checkbox: { width: '20px', height: '20px' },
  modalActions: { display: 'flex', gap: '1rem', marginTop: '1rem' },
  submitBtn: { flex: 1, padding: '0.75rem', backgroundColor: '#27ae60', backgroundImage: 'linear-gradient(135deg, #27ae60 0%, #219a52 100%)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', cursor: 'pointer' },
  cancelBtn: { flex: 1, padding: '0.75rem', backgroundColor: '#374151', color: '#e2e8f0', border: '1px solid #4b5563', borderRadius: '8px', fontSize: '16px', cursor: 'pointer' },
};

export default Users;
